import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '../../../components/ui/Dialog';
import type { ClawbackPreview } from '@/models/tournament';
import ClawbackSummaryList from './ClawbackSummaryList';

/**
 * CO-921/CO-922 Round 2: the one confirmation HR sees before pay already given to officials is taken back.
 * Driven by `useClawbackConsent` (spread its `dialogProps`). Opens on top of whatever dialog triggered the
 * change, so declining leaves that dialog, and HR's edits in it, exactly as they were.
 *
 * Outside clicks never dismiss it: a consent must be an explicit choice. While the approved request is in
 * flight every dismissal path (X, Esc, Cancel, Skip) is locked together (ui-standards.md).
 */

export interface ClawbackConsentDialogProps {
  open: boolean;
  preview: ClawbackPreview | null;
  /** The server refused an earlier approval because the amounts moved; the preview is the fresh one. */
  stale: boolean;
  /** Bulk import: adds "Import the other rows only". */
  bulk: boolean;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onSkip: () => void;
  onCancel: () => void;
}

const ClawbackConsentDialog: React.FC<ClawbackConsentDialogProps> = ({
  open,
  preview,
  stale,
  bulk,
  busy,
  error,
  onConfirm,
  onSkip,
  onCancel,
}) => {
  const { t } = useTranslation('tournament');
  if (!open || !preview) return null;

  return (
    <Dialog open onOpenChange={(next) => { if (!next && !busy) onCancel(); }}>
      <DialogContent
        size="default"
        data-testid="clawback-consent-dialog"
        hideCloseButton={busy}
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        aria-busy={busy}
      >
        <DialogHeader className="py-4">
          <div className="flex items-start gap-3 pe-6">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/30">
              <Icon name="HandCoins" size={20} className="text-red-600 dark:text-red-400" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="font-bold" data-testid="clawback-consent-title">
                {t('Take back pay from officials?')}
              </DialogTitle>
              <DialogDescription>
                {t('This change takes back money already paid to the officials below. Nothing is saved unless you confirm.')}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {stale && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
              data-testid="clawback-consent-stale"
            >
              <Icon name="RefreshCw" size={16} className="mt-0.5 shrink-0" />
              <p>{t('The amounts changed since you opened this. Review the new amounts.')}</p>
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200"
              data-testid="clawback-consent-error"
            >
              <Icon name="AlertCircle" size={16} className="mt-0.5 shrink-0" />
              <p className="min-w-0 break-words">{error}</p>
            </div>
          )}

          <ClawbackSummaryList
            officials={preview.officials}
            totals={preview.totalsByCurrency}
            testIdPrefix="clawback-consent"
          />

          {bulk && (
            <p className="text-sm text-muted-foreground">
              {t('Importing the other rows only leaves these matches as they are. You can approve them later.')}
            </p>
          )}
        </DialogBody>

        <DialogFooter className="flex-wrap gap-2 rounded-b-xl bg-muted/50 py-4">
          <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="clawback-consent-cancel-btn">
            {t('Keep everything as it is')}
          </Button>
          {bulk && (
            <Button variant="outline" onClick={onSkip} disabled={busy} data-testid="clawback-consent-skip-btn">
              {t('Import the other rows only')}
            </Button>
          )}
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={busy}
            loading={busy}
            data-testid="clawback-consent-confirm-btn"
          >
            {busy ? t('Saving...') : bulk ? t('Take back and import') : t('Take back and save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ClawbackConsentDialog;
